import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { OrgsMenu, ORG_LINKS } from "@/components/OrgsMenu";
import { PulseIcon, GearSixIcon, SignOutIcon, BuildingsIcon, UsersThreeIcon, ListIcon, XIcon, QuestionIcon, WaveSineIcon, CodeIcon, BroadcastIcon, ChartLineUpIcon, ShieldCheckIcon, FileTextIcon } from "@phosphor-icons/react";

export default function Header() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [newItems, setNewItems] = useState([]);
  const [newOpen, setNewOpen] = useState(false);

  // "N new this week" — new-concern signals newer than what this reader last saw. The nav cue is now
  // the single global entry point to "what's new" (homepage strip + standalone Signals page retired).
  useEffect(() => {
    let seen = "";
    try { seen = localStorage.getItem("embi_recap_seen") || ""; } catch {}
    api.get("/whats-new").then((r) => {
      const items = r.data.items || [];
      setNewItems(seen ? items.filter((it) => it.created_at && it.created_at > seen) : []);
    }).catch(() => {});
  }, []);

  const newCount = newItems.length;

  const openItem = (it) => {
    setNewOpen(false);
    setMenuOpen(false);
    // mark everything seen so the cue clears once they've looked
    try {
      const latest = newItems.reduce((m, x) => (x.created_at > m ? x.created_at : m), "");
      if (latest) localStorage.setItem("embi_recap_seen", latest);
    } catch {}
    setNewItems([]);
    nav(it.target_type === "index" ? `/index/${it.ticker}` : `/topic/${it.target_key}`);
  };

  const now = new Date().toUTCString().replace("GMT", "UTC");
  const isGov = !!user?.is_gov;
  const govContext = isGov || loc.pathname.startsWith("/embi-gov") || loc.pathname.startsWith("/gov");

  let links;
  if (govContext) {
    links = isGov
      ? [
          { to: "/portal", label: "Workspace", testid: "nav-portal", icon: BuildingsIcon },
          { to: "/gov/reports", label: "Reports", testid: "nav-gov-reports", icon: FileTextIcon },
        ]
      : [];
  } else {
    links = [{ to: "/", label: "AI Impact", testid: "nav-exchange" }];
    links.push({ to: "/topics", label: "Topics", testid: "nav-markets" });
    if (user) {
      links.push({ to: "/portal", label: "Workspace", testid: "nav-portal", icon: BuildingsIcon });
      links.push({ to: "/organization", label: "Organization", testid: "nav-organization", icon: UsersThreeIcon });
      if (user.role === "admin") {
        links.push({ to: "/admin", label: "Ingest", testid: "nav-admin", icon: GearSixIcon });
        links.push({ to: "/markets", label: "Analyst view", testid: "nav-analyst", icon: ChartLineUpIcon });
      }
    }
    links.push({ to: "/pricing", label: "Pricing", testid: "nav-pricing" });
    links.push({ to: "/developers", label: "Data & API", testid: "nav-developers", icon: CodeIcon });
    links.push({ to: "/faq", label: "FAQ", testid: "nav-faq", icon: QuestionIcon });
  }

  const communitiesActive = ["/newsrooms", "/for-communities", "/associations"].includes(loc.pathname);

  const activeCls = govContext ? "text-[#7FB0E6] bg-[#4F84C4]/15" : "text-signal bg-signal/10";
  const linkClass = (to) =>
    `px-3 py-1.5 rounded text-[14px] flex items-center gap-1.5 transition-colors ${loc.pathname === to ? activeCls : "text-zinc-400 hover:text-zinc-100"}`;

  const doLogout = async () => { setMenuOpen(false); await logout(); nav("/"); };

  return (
    <header className="sticky top-0 z-40 border-b border-zinc-800 bg-[#061025]/90 backdrop-blur-md">
      <div className="flex items-center justify-between px-4 sm:px-6 h-16">
        <Link to={govContext ? (isGov ? "/portal" : "/embi-gov") : "/"} data-testid="brand-link" onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 group min-w-0">
          <PulseIcon size={28} weight="bold" className={`${govContext ? "text-[#4F84C4]" : "text-signal drop-shadow-[0_0_10px_rgba(38,241,149,0.5)]"} group-hover:scale-110 transition-transform shrink-0`} />
          <div className="leading-none min-w-0">
            {govContext ? (
              <>
                <div className="font-semibold tracking-tight text-[15px] sm:text-[19px] text-zinc-50 truncate">EMBI<span className="text-[#4F84C4]">-Gov</span><span className="align-super text-[10px] sm:text-[11px] text-[#4F84C4] ml-0.5">℠</span></div>
                <div className="font-mono text-[11px] sm:text-[12px] uppercase tracking-[0.22em] sm:tracking-[0.28em] text-zinc-400 mt-0.5 truncate">Operational Intelligence</div>
              </>
            ) : (
              <>
                <div className="font-semibold tracking-tight text-[15px] sm:text-[19px] text-zinc-50 truncate">EMBI<span className="align-super text-[10px] sm:text-[11px] text-signal ml-0.5">℠</span> AI Impact</div>
                <div className="font-mono text-[11px] sm:text-[12px] uppercase tracking-[0.22em] sm:tracking-[0.28em] text-zinc-500 mt-0.5 truncate">The Kitchen-Table View of AI</div>
              </>
            )}
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-2 font-mono text-[12px] text-zinc-500">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 live-dot" />
          <span className="text-emerald-400">LIVE</span>
          <span className="text-zinc-700">·</span>
          <span>{now}</span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {newCount > 0 && (
            <div className="relative mr-1">
              <button data-testid="nav-new-count" onClick={() => setNewOpen(!newOpen)}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#a78bfa]/45 bg-[#a78bfa]/10 px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.12em] text-[#c4b5fd] hover:bg-[#a78bfa]/[0.18] transition-colors">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#a78bfa] opacity-70 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#a78bfa]" />
                </span>
                {newCount} new this week
              </button>
              {newOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNewOpen(false)} />
                  <div data-testid="nav-new-panel" className="absolute right-0 mt-2 w-80 z-50 rounded-xl border border-[#22304f] bg-[#0c1430] shadow-2xl p-2">
                    <div className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#a78bfa]">New this week</div>
                    {newItems.map((it) => (
                      <button key={it.id} data-testid={`nav-new-item-${it.ticker}`} onClick={() => openItem(it)}
                        className="w-full text-left rounded-lg px-2 py-2 hover:bg-white/[0.04] transition-colors">
                        <div className="text-[13.5px] text-zinc-100 leading-snug">{it.headline}</div>
                        {it.blurb && <div className="text-[12px] text-zinc-500 leading-snug mt-0.5 line-clamp-2">{it.blurb}</div>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {links.map((l) => (
            <Link key={l.testid} to={l.to} data-testid={l.testid} className={linkClass(l.to)}>
              {l.icon && <l.icon size={15} />} {l.label}
            </Link>
          ))}
          {!govContext && <OrgsMenu active={communitiesActive} />}
          {user ? (
            <>
              {(user.is_pro || user.effective_pro) && (
                <span data-testid="pro-badge" className="hidden lg:inline font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-signal/15 text-signal border border-signal/30 font-bold">Professional</span>
              )}
              {user.is_demo ? (
                <span data-testid="nav-user" className="hidden lg:inline px-2 font-mono text-[12px] text-zinc-500 truncate max-w-[140px]">{user.name || user.email}</span>
              ) : (
                <Link to="/account" data-testid="nav-user" className="hidden lg:inline px-2 font-mono text-[12px] text-zinc-500 hover:text-signal truncate max-w-[140px] transition-colors">{user.name || user.email}</Link>
              )}
              <button data-testid="logout-btn" onClick={doLogout} className="px-3 py-1.5 rounded text-[14px] text-zinc-400 hover:text-rose-400 flex items-center gap-1.5 transition-colors">
                <SignOutIcon size={15} /> Exit
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login" className="px-3 py-1.5 rounded text-[14px] text-zinc-400 hover:text-zinc-100 transition-colors">Sign in</Link>
          )}
        </nav>

        {/* Mobile hamburger */}
        <div className="md:hidden flex items-center gap-1">
          {newCount > 0 && (
            <button data-testid="nav-new-count-mobile" onClick={() => newItems[0] && openItem(newItems[0])}
              className="inline-flex items-center gap-1 rounded-full border border-[#a78bfa]/45 bg-[#a78bfa]/10 px-2 py-1 text-[11px] font-mono text-[#c4b5fd]">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#a78bfa] opacity-70 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#a78bfa]" />
              </span>
              {newCount}
            </button>
          )}
          {!user && (
            <Link to="/login" data-testid="nav-login-mobile" className="px-3 py-1.5 rounded text-[14px] text-zinc-300">Sign in</Link>
          )}
          <button data-testid="mobile-menu-toggle" aria-label="Menu" onClick={() => setMenuOpen((o) => !o)} className="p-2 rounded text-zinc-300 hover:text-signal hover:bg-zinc-800/60 transition-colors">
            {menuOpen ? <XIcon size={22} weight="bold" /> : <ListIcon size={22} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown panel */}
      {menuOpen && (
        <nav data-testid="mobile-menu" className="md:hidden border-t border-zinc-800 bg-[#061025] px-3 py-3 space-y-1">
          {links.map((l) => (
            <Link key={l.testid} to={l.to} data-testid={`${l.testid}-mobile`} onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded text-[15px] ${loc.pathname === l.to ? activeCls : "text-zinc-300 hover:bg-zinc-800/60"}`}>
              {l.icon && <l.icon size={17} />} {l.label}
            </Link>
          ))}
          {!govContext && (
          <div className="rounded-lg border border-signal/30 bg-signal/[0.06] p-1.5" data-testid="nav-orgs-mobile">
            <div className="px-2 pt-1 pb-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-signal">For Organizations · choose your path</div>
            {ORG_LINKS.map(({ to, label, Icon, testid, tint }) => (
              <Link key={to} to={to} data-testid={`${testid}-mobile`} onClick={() => setMenuOpen(false)}
                style={{ borderLeft: `3px solid ${tint}` }}
                className={`flex items-center gap-2.5 px-2.5 py-2 mb-1 last:mb-0 rounded-md text-[14px] font-semibold text-zinc-100 ${loc.pathname === to ? "bg-signal/15" : "hover:bg-signal/10"}`}>
                <Icon size={18} weight="duotone" className="shrink-0" style={{ color: tint }} /> {label}
              </Link>
            ))}
          </div>
          )}
          {user && (
            <>
              {!user.is_demo && (
              <Link to="/account" data-testid="nav-account-mobile" onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2.5 rounded text-[15px] text-zinc-300 hover:bg-zinc-800/60 border-t border-zinc-800 mt-1">
                <ShieldCheckIcon size={17} /> Account &amp; privacy
              </Link>
              )}
              <button data-testid="logout-btn-mobile" onClick={doLogout} className="w-full flex items-center gap-2 px-3 py-2.5 rounded text-[15px] text-zinc-300 hover:text-rose-400 hover:bg-zinc-800/60">
                <SignOutIcon size={17} /> Exit
              </button>
            </>
          )}
        </nav>
      )}
    </header>
  );
}
